import { API_ENDPOINTS } from "config/apiConfig";
import type {
  AdminBadge,
  AdminCompanyQuestProgressDetail,
  AdminCompanyQuestProgressPage,
  AdminQuest,
  CreateQuestPayload,
  QuestProgressType,
} from "common/types/questManagement.type";
import { baseApi } from "./baseApi";

interface QuestProgressParams {
  type: QuestProgressType;
  page: number;
  limit: number;
}

interface QuestProgressDetailParams {
  companyId: string;
  type: QuestProgressType;
}

const EMPTY_PROGRESS_PAGE: AdminCompanyQuestProgressPage = {
  items: [],
  meta: {
    items_count: 0,
    total_items: 0,
    total_pages: 1,
    items_per_page: 10,
    current_page: 1,
  },
};

const getResponseData = <T>(payload: unknown): T | null => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in payload &&
    typeof (payload as { data?: unknown }).data === "object" &&
    (payload as { data?: unknown }).data !== null
  ) {
    return (payload as { data: T }).data;
  }

  return null;
};

const getResponseDataArray = <T>(payload: unknown): T[] => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in payload &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: T[] }).data;
  }

  return [];
};

const getProgressResponseData = (
  payload: unknown,
): AdminCompanyQuestProgressPage => {
  return (
    getResponseData<AdminCompanyQuestProgressPage>(payload) ??
    EMPTY_PROGRESS_PAGE
  );
};

const getProgressDetailResponseData = (
  payload: unknown,
): AdminCompanyQuestProgressDetail | null => {
  const data = getResponseData<AdminCompanyQuestProgressDetail>(payload);
  if (data) return data;

  if (typeof payload === "object" && payload !== null) {
    return payload as AdminCompanyQuestProgressDetail;
  }

  return null;
};

const getNextQuestCodeResponseData = (payload: unknown): string => {
  const data = getResponseData<{ code?: unknown }>(payload);
  if (typeof data?.code === "string") return data.code;

  if (typeof payload === "object" && payload !== null && "code" in payload) {
    const code = (payload as { code?: unknown }).code;
    return typeof code === "string" ? code : "";
  }

  return "";
};

export const questManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminQuests: builder.query<AdminQuest[], void>({
      query: () => ({
        url: API_ENDPOINTS.ADMIN_QUESTS,
      }),
      transformResponse: (response: unknown) =>
        getResponseDataArray<AdminQuest>(response),
      providesTags: ["QuestManagement"],
    }),

    getAdminBadges: builder.query<AdminBadge[], void>({
      query: () => ({
        url: API_ENDPOINTS.ADMIN_BADGES,
      }),
      transformResponse: (response: unknown) =>
        getResponseDataArray<AdminBadge>(response),
      providesTags: ["QuestManagement"],
    }),

    getAdminQuestNextCode: builder.query<string, void>({
      query: () => ({
        url: API_ENDPOINTS.ADMIN_QUEST_NEXT_CODE,
      }),
      transformResponse: getNextQuestCodeResponseData,
    }),

    getAdminCompanyQuestProgress: builder.query<
      AdminCompanyQuestProgressPage,
      QuestProgressParams
    >({
      query: (params) => ({
        url: API_ENDPOINTS.ADMIN_COMPANY_QUEST_PROGRESS,
        params,
      }),
      transformResponse: getProgressResponseData,
      providesTags: ["QuestManagement"],
    }),

    getAdminCompanyQuestProgressDetail: builder.query<
      AdminCompanyQuestProgressDetail | null,
      QuestProgressDetailParams
    >({
      query: ({ companyId, type }) => ({
        url: `${API_ENDPOINTS.ADMIN_COMPANIES}/${companyId}/quest-progress`,
        params: { type },
      }),
      transformResponse: getProgressDetailResponseData,
      providesTags: ["QuestManagement"],
    }),

    createAdminQuest: builder.mutation<AdminQuest, CreateQuestPayload>({
      query: (body) => ({
        url: API_ENDPOINTS.ADMIN_QUESTS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["QuestManagement"],
    }),

    updateAdminQuest: builder.mutation<
      AdminQuest,
      { questId: string | number; payload: Omit<CreateQuestPayload, "code"> }
    >({
      query: ({ questId, payload }) => ({
        url: `${API_ENDPOINTS.ADMIN_QUESTS}/${questId}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["QuestManagement"],
    }),

    deleteAdminQuest: builder.mutation<AdminQuest, string | number>({
      query: (questId) => ({
        url: `${API_ENDPOINTS.ADMIN_QUESTS}/${questId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["QuestManagement"],
    }),

    updateAdminQuestStatus: builder.mutation<
      AdminQuest,
      { questId: string | number; is_active: boolean }
    >({
      query: ({ questId, is_active }) => ({
        url: `${API_ENDPOINTS.ADMIN_QUESTS}/${questId}`,
        method: "PATCH",
        body: { is_active },
      }),
      invalidatesTags: ["QuestManagement"],
    }),
  }),
});

export const {
  useGetAdminQuestsQuery,
  useGetAdminBadgesQuery,
  useLazyGetAdminQuestNextCodeQuery,
  useGetAdminCompanyQuestProgressQuery,
  useGetAdminCompanyQuestProgressDetailQuery,
  useCreateAdminQuestMutation,
  useUpdateAdminQuestMutation,
  useDeleteAdminQuestMutation,
  useUpdateAdminQuestStatusMutation,
} = questManagementApi;
